import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { authService } from './services';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Playlists from './pages/Playlists';
import PlaylistDetail from './pages/PlaylistDetail';
import Prizes from './pages/PrizesWithCart';
import Profile from './pages/Profile';
import MarshmallowHome from './pages/MarshmallowHome';
import MarshmallowWrite from './pages/MarshmallowWrite';
import MarshmallowList from './pages/MarshmallowList';
import MarshmallowAdmin from './pages/MarshmallowAdmin';
import BotConfig from './pages/BotConfig';
import AiTokenQuotaAdmin from './pages/AiTokenQuotaAdmin';
import QQBotConfig from './pages/QQBotConfig';
import PermissionManagement from './pages/PermissionManagement';
import BlindBoxStats from './pages/BlindBoxStats';
import BlindBoxOverlay from './pages/BlindBoxOverlay';
import GiftScreenshot from './pages/GiftScreenshot';
import GiftOverlay from './pages/GiftOverlay';
import GiftOverlaySettings from './pages/GiftOverlaySettings';
import AdminPoints from './pages/AdminPoints';
import AdminPrizes from './pages/AdminPrizes';
import AdminPrizeOrders from './pages/AdminPrizeOrders';
import AdminMovieTickets from './pages/AdminMovieTickets';
import SessionExpiredModal from './components/SessionExpiredModal';
import { FeedbackProvider } from './components/FeedbackProvider';
import './styles/App.css';

// Component to verify session validity on route change/app load
const AuthVerifier = () => {
  const location = useLocation();
  const [isSessionExpired, setIsSessionExpired] = React.useState(false);

  useEffect(() => {
    const handleAuthExpired = () => {
      setIsSessionExpired(true);
    };

    window.addEventListener('auth:expired', handleAuthExpired);

    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired);
    };
  }, []);

  useEffect(() => {
    const verifySession = async () => {
      // Only verify if we have a token and we are not already on the login page
      if (authService.isAuthenticated() && location.pathname !== '/login') {
        try {
          await authService.getProfile();
        } catch (error) {
          // 401 errors are handled by the api interceptor in services/api.js
        }
      }
    };

    verifySession();
  }, [location.pathname]);

  const handleCloseModal = () => {
    setIsSessionExpired(false);
    // Clear local storage and redirect to login
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return <SessionExpiredModal isOpen={isSessionExpired} onClose={handleCloseModal} />;
};

function App() {
  return (
    <Router>
      <AuthVerifier />
      <Routes>
        {/* OBS 透明浏览器源 - 无 Navbar */}
        <Route path="/live/blindbox/:roomId" element={<BlindBoxOverlay />} />
        <Route path="/live/gifts/:accessToken" element={<GiftOverlay />} />

        {/* 其他所有页面 - 有 Navbar */}
        <Route path="/*" element={
          <FeedbackProvider>
            <div className="App">
              <Navbar />
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/playlists" element={<Playlists />} />
                <Route path="/playlists/:id" element={<PlaylistDetail />} />
                <Route path="/marshmallows" element={<MarshmallowHome />} />
                <Route path="/marshmallows/write" element={<MarshmallowWrite />} />
                <Route
                  path="/marshmallows/my"
                  element={
                    <ProtectedRoute>
                      <MarshmallowList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/marshmallows"
                  element={
                    <ProtectedRoute requiredPermissions={['marshmallow.manage']}>
                      <MarshmallowAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/bot"
                  element={
                    <ProtectedRoute requiredPermissions={['bot.admin', 'bot.room.view']}>
                      <BotConfig />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/bot/ai-tokens"
                  element={
                    <ProtectedRoute requiredPermissions={['bot.admin']}>
                      <AiTokenQuotaAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/bot/qq"
                  element={
                    <ProtectedRoute requiredPermissions={['bot.admin', 'bot.room.view']}>
                      <QQBotConfig />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/blindbox"
                  element={
                    <ProtectedRoute requiredPermissions={['bot.admin', 'bot.room.view']}>
                      <BlindBoxStats />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/gift-screenshot"
                  element={
                    <ProtectedRoute requiredPermissions={['bot.admin', 'bot.room.view']}>
                      <GiftScreenshot />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/gift-overlay"
                  element={
                    <ProtectedRoute requiredPermissions={['bot.admin', 'bot.room.view']}>
                      <GiftOverlaySettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/permissions"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <PermissionManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/points"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminPoints />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/movie-tickets"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminMovieTickets />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/prizes/new"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminPrizes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/prizes/:prizeId/edit"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminPrizes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/prizes"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminPrizes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/prize-orders"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminPrizeOrders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/prizes"
                  element={
                    <ProtectedRoute>
                      <Prizes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </div>
          </FeedbackProvider>
        } />
      </Routes>
    </Router>
  );
}

export default App;
